import { Component, OnInit, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { CardModule } from 'primeng/card'
import { DividerModule } from 'primeng/divider'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { TooltipModule } from 'primeng/tooltip'
import { ToastModule } from 'primeng/toast'
import { MessageService } from 'primeng/api'
import { InterventionEditModel } from '../../../../core/models/intervention-edit.model'
import { InterventionHeaderComponent } from '../intervention-header/intervention-header.component'
import { InterventionTechniciansComponent } from '../intervention-technicians/intervention-technicians.component'
import { InterventionDetailsComponent } from '../intervention-details/intervention-details.component'
import { InterventionProductsComponent } from '../intervention-products/intervention-products.component'
import { Router, ActivatedRoute } from '@angular/router'
import { InterventionsService } from '../../services/interventions.service'
import { catchError, finalize, of, forkJoin } from 'rxjs'
import { InterventionStatusEnum } from '../../../../core/enums/intervention-status'
import { ToastService } from '../../../../shared/services/toast.service'
import { InterventionContactsComponent } from '../intervention-contacts/intervention-contacts.component'
import { ShiftModalComponent } from '../shift-modal/shift-modal.component'
import { ShiftOriginModel } from '../../../../core/models/shift-origin.model'
import { UserModel } from '../../../../core/models/user.model'
import { UserService } from '../../../users/services/user.service'
import { UnprogrammingModalComponent } from '../unprogramming-modal/unprogramming-modal.component'
import { SendMailModalComponent } from '../send-mail-modal/send-mail-modal.component'
import { environment } from '../../../../../environments/environment'
import { MailsService } from '../../../mails/services/mails.service'
import { AffairMailsComponent } from '../../../affairs/components/affair/affair-mails/affair-mails.component'

@Component({
  selector: 'app-intervention-view',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    DividerModule,
    ButtonModule,
    ProgressSpinnerModule,
    TooltipModule,
    ToastModule,
    InterventionHeaderComponent,
    InterventionTechniciansComponent,
    InterventionDetailsComponent,
    InterventionProductsComponent,
    InterventionContactsComponent,
    ShiftModalComponent,
    UnprogrammingModalComponent,
    SendMailModalComponent,
    AffairMailsComponent,
  ],
  providers: [MessageService],
  templateUrl: './intervention-view.component.html',
  styleUrls: ['./intervention-view.component.scss'],
})
export class InterventionViewComponent implements OnInit {
  intervention: InterventionEditModel | null = null
  loading: boolean = true
  error: string | null = null

  public router = inject(Router)
  private route = inject(ActivatedRoute)
  private interventionsService = inject(InterventionsService)
  private messageService = inject(MessageService)
  private toastService = inject(ToastService)
  private userService = inject(UserService)
  private mailsService = inject(MailsService)

  confirmLoading: boolean = false
  waitingConfirmationLoading: boolean = false
  interventionStatusEnum = InterventionStatusEnum

  // Propriétés pour la modale de décalage
  showShiftModal: boolean = false
  shiftModalLoading: boolean = false
  shiftOrigins: ShiftOriginModel[] = []
  shiftSelectedEvent: any = null
  allTechnicians: UserModel[] = []

  // Propriétés pour la modale de déprogrammation
  showUnprogrammingModal: boolean = false
  unprogrammingLoading: boolean = false
  unprogrammingSelectedEvent: any = null

  // Propriétés pour la modale d'envoi de mail
  showSendMailModal: boolean = false

  // Propriétés pour les mails de l'affaire
  mails: any[] = []
  mailsLoading: boolean = false

  ngOnInit(): void {
    this.loadInterventionData()
  }

  /**
   * Charge les données de l'intervention à partir des paramètres de route
   */
  private loadInterventionData(): void {
    this.loading = true
    this.error = null

    // Récupérer les paramètres de route
    const interventionId = this.route.snapshot.paramMap.get('id')
    const erpSource = this.route.snapshot.paramMap.get('erpSource')

    if (!interventionId || !erpSource) {
      this.error = "Paramètres d'URL manquants"
      this.loading = false
      return
    }

    // Charger l'intervention, les origines de décalage et les techniciens en parallèle
    const interventionRequest = this.interventionsService.show(
      +interventionId,
      erpSource
    )
    const originsRequest = this.interventionsService.getShiftOrigins(erpSource)
    const techniciansRequest = this.userService.getUsersWithFilters({})

    // Utiliser forkJoin pour exécuter toutes les requêtes en parallèle
    forkJoin({
      intervention: interventionRequest.pipe(
        catchError((err) => {
          console.error("Erreur lors du chargement de l'intervention", err)
          this.error = `Impossible de charger l'intervention: ${err.error?.message || err.message || 'Erreur inconnue'}`
          return of(null)
        })
      ),
      origins: originsRequest.pipe(
        catchError((err) => {
          console.warn(
            'Erreur lors du chargement des origines de décalage',
            err
          )
          return of([])
        })
      ),
      technicians: techniciansRequest.pipe(
        catchError((err) => {
          console.warn('Erreur lors du chargement des techniciens', err)
          return of([])
        })
      ),
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (results) => {
          if (results.intervention) {
            this.intervention =
              results.intervention as unknown as InterventionEditModel
            // Charger les mails de l'affaire
            this.loadMails()
          }
          this.shiftOrigins = results.origins as ShiftOriginModel[]
          this.allTechnicians = results.technicians
        },
      })
  }

  /**
   * Charge les mails de l'affaire
   */
  loadMails(): void {
    if (!this.intervention?.affair?.mlRef) return

    this.mailsLoading = true
    this.mailsService
      .getMailsByMlRef(this.intervention.affair.mlRef)
      .pipe(
        catchError((err) => {
          console.warn('Erreur lors du chargement des mails', err)
          return of([])
        }),
        finalize(() => (this.mailsLoading = false))
      )
      .subscribe((mails) => {
        // Trier du plus récent au plus ancien
        this.mails = mails.sort((a: any, b: any) => {
          const dateA = new Date(a.createdAt || a.sentDate || 0).getTime()
          const dateB = new Date(b.createdAt || b.sentDate || 0).getTime()
          return dateB - dateA
        })
      })
  }

  /**
   * Appelé après l'envoi d'un mail - recharge la liste des mails
   */
  onMailSent(): void {
    this.loadMails()
  }

  /**
   * Naviguer vers la page d'édition de l'intervention
   */
  editIntervention(): void {
    if (this.intervention?.id) {
      // Récupérer erpSource depuis l'URL actuelle
      const erpSource = this.route.snapshot.paramMap.get('erpSource')
      this.router.navigate([
        '/interventions',
        this.intervention.id,
        'edit',
        erpSource,
      ])
    }
  }

  /**
   * Naviguer vers la page d'affaire associée
   */
  navigateToAffair(): void {
    if (this.intervention?.affair?.mlRef) {
      this.router.navigate(['/affair', this.intervention.affair.mlRef])
    }
  }

  /**
   * Rafraîchit les données de l'intervention
   */
  refresh(): void {
    this.loadInterventionData()
  }

  /**
   * Vérifie si l'intervention est en attente de confirmation
   */
  get isWaitingConfirmation(): boolean {
    return (
      this.intervention?.interventionStatus?.id ===
      InterventionStatusEnum.INTERVENTION_STATUS_WAITING_CONFIRMATION
    )
  }

  /**
   * Vérifie si l'intervention est confirmée
   */
  get isConfirmed(): boolean {
    return (
      this.intervention?.interventionStatus?.id ===
      InterventionStatusEnum.INTERVENTION_STATUS_CONFIRMED
    )
  }

  /**
   * Confirme l'intervention et met à jour son statut
   */
  confirmIntervention(): void {
    if (!this.intervention?.id) return

    const erpSource = this.route.snapshot.paramMap.get('erpSource')
    if (!erpSource) return

    this.confirmLoading = true

    this.interventionsService
      .confirm(this.intervention.id, { erpSource })
      .pipe(
        catchError((err) => {
          this.toastService.showError(
            err.error?.message || err.message || 'Erreur inconnue'
          )
          return of(null)
        }),
        finalize(() => {
          this.confirmLoading = false
        })
      )
      .subscribe((response) => {
        if (response) {
          this.toastService.showSuccess('Intervention confirmée avec succès')
          this.refresh()
        }
      })
  }

  /**
   * Marque l'intervention comme "en attente de confirmation"
   */
  markAsWaitingConfirmation(): void {
    if (!this.intervention?.id) return

    const erpSource = this.route.snapshot.paramMap.get('erpSource')
    if (!erpSource) return

    this.waitingConfirmationLoading = true

    this.interventionsService
      .markAsWaitingConfirmation(this.intervention.id, erpSource)
      .pipe(
        catchError((err) => {
          this.toastService.showError(
            err.error?.message ||
              err.message ||
              'Erreur lors de la modification du statut'
          )
          return of(null)
        }),
        finalize(() => {
          this.waitingConfirmationLoading = false
        })
      )
      .subscribe((response) => {
        if (response) {
          this.toastService.showSuccess(
            'Intervention marquée comme "en attente de confirmation"'
          )
          this.refresh()
        }
      })
  }

  /**
   * Ouvre la modale de décalage d'intervention
   */
  openShiftModal(): void {
    if (!this.intervention) return

    const erpSource = this.route.snapshot.paramMap.get('erpSource')
    if (!erpSource) return

    // Créer un événement formaté comme attendu par le composant shift-modal
    this.shiftSelectedEvent = {
      id: this.intervention.id,
      title: this.intervention.name,
      start: new Date(this.intervention.startDate),
      end: new Date(this.intervention.endDate),
      extendedProps: {
        data: {
          id: this.intervention.id,
          erpSource: erpSource,
          isTimeSlot: this.intervention.isTimeSlot,
          startTimeSlot: this.intervention.startTimeSlot,
          endTimeSlot: this.intervention.endTimeSlot,
          usersTechnicians: this.intervention.usersTechnicians,
        },
      },
    }

    // Ouvrir directement la modale puisque les données sont déjà chargées
    this.showShiftModal = true
  }

  /**
   * Gère la soumission du formulaire de décalage
   */
  handleShiftSubmit(formData: any): void {
    if (!this.intervention?.id) return

    this.shiftModalLoading = true

    this.interventionsService
      .shift(this.intervention.id, formData)
      .pipe(
        catchError((err) => {
          this.toastService.showError(
            "Erreur lors du décalage de l'intervention"
          )
          return of(null)
        }),
        finalize(() => {
          this.shiftModalLoading = false
          this.showShiftModal = false
        })
      )
      .subscribe((response) => {
        if (response) {
          this.toastService.showSuccess('Intervention décalée avec succès')
          this.refresh()
        }
      })
  }

  /**
   * Vérifie si l'intervention peut être déprogrammée
   * (toute intervention qui n'est pas annulée ou facturée)
   */
  get canBeUnprogrammed(): boolean {
    if (!this.intervention?.interventionStatus?.id) return false

    return ![
      InterventionStatusEnum.INTERVENTION_STATUS_CANCELED,
      InterventionStatusEnum.INTERVENTION_STATUS_BILLED,
    ].includes(this.intervention.interventionStatus.id)
  }

  /**
   * Ouvre la modale de déprogrammation
   */
  openUnprogrammingModal(): void {
    if (!this.intervention) return

    const erpSource = this.route.snapshot.paramMap.get('erpSource')
    if (!erpSource) return

    this.unprogrammingSelectedEvent = {
      id: this.intervention.id,
      title: this.intervention.name,
      extendedProps: {
        data: {
          id: this.intervention.id,
          erpSource: erpSource,
        },
      },
    }

    this.showUnprogrammingModal = true
  }

  /**
   * Gère la soumission du formulaire de déprogrammation
   */
  handleUnprogrammingSubmit(formData: any): void {
    if (!this.intervention?.id) return

    const erpSource = this.route.snapshot.paramMap.get('erpSource')
    if (!erpSource) return

    this.unprogrammingLoading = true

    this.interventionsService
      .deprogram(this.intervention.id, {
        origin: formData.origin,
        reason: formData.reason,
        erpSource: erpSource,
      })
      .pipe(
        catchError((err) => {
          this.toastService.showError(
            err.error?.message ||
              err.message ||
              "Erreur lors de la déprogrammation de l'intervention"
          )
          return of(null)
        }),
        finalize(() => {
          this.unprogrammingLoading = false
          this.showUnprogrammingModal = false
        })
      )
      .subscribe((response) => {
        if (response) {
          this.toastService.showSuccess('Intervention déprogrammée avec succès')
          // Rediriger vers la liste des interventions après déprogrammation
          this.router.navigate(['/interventions-incoming'])
        }
      })
  }

  openOnEros() {
    const erpSource = this.route.snapshot.paramMap.get('erpSource')
    if (erpSource === 'abm') {
      window.open(
        environment.abmUrl + 'interventions/' + this.intervention?.id + '/edit',
        '_blank'
      )
    } else if (erpSource === 'nrj') {
      window.open(
        environment.nrjUrl + 'interventions/' + this.intervention?.id + '/edit',
        '_blank'
      )
    }
  }

  /**
   * Navigue vers la page de replanification de l'intervention
   */
  rescheduleIntervention(): void {
    if (!this.intervention?.id) return

    const erpSource = this.route.snapshot.paramMap.get('erpSource')
    if (!erpSource) return

    this.router.navigate([
      '/intervention',
      this.intervention.id,
      'reschedule',
      erpSource,
    ])
  }

  /**
   * Ouvre la modale d'envoi de mail
   */
  openSendMailModal(): void {
    this.showSendMailModal = true
  }

  /**
   * Récupère l'erpSource depuis l'URL
   */
  get currentErpSource(): string | null {
    return this.route.snapshot.paramMap.get('erpSource')
  }
}
